import { Extension } from '@tiptap/core'

type LineHeightAllowedTypes = 'normal' | 'inherit' | 'initial' | number

export type LineHeightOptions = {
  defaultLineHeight?: LineHeightAllowedTypes
  lineHeight?: LineHeightAllowedTypes
  types: string[]
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    lineHeight: {
      setLineHeight: (options: LineHeightAllowedTypes) => ReturnType
    }
  }
}

export const CustomLineHeight = Extension.create<LineHeightOptions>({
  name: 'lineHeight',

  addOptions() {
    return {
      types: ['paragraph', 'heading', 'blockquote', 'listItem'],
      defaultLineHeight: 1.5,
    }
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          lineHeight: {
            default: this.options.defaultLineHeight,
            parseHTML: (element: HTMLElement) => {
              return element.style.lineHeight || this.options.defaultLineHeight
            },
            renderHTML: attributes => {
              return {
                style: `line-height: ${
                  attributes.lineHeight || this.options.defaultLineHeight
                }`,
              }
            },
          },
        },
      },
    ]
  },

  addCommands() {
    return {
      setLineHeight:
        (value: LineHeightAllowedTypes) =>
        ({ commands }) => {
          return this.options.types.every(type =>
            commands.updateAttributes(type, { lineHeight: value })
          )
        },
    }
  },

  onCreate() {
    // Set default line height on editor creation
    this.options.types.forEach(type => {
      const attrs = this.editor.getAttributes(type)
      if (!attrs.lineHeight) {
        this.editor.commands.updateAttributes(type, {
          lineHeight: this.options.defaultLineHeight,
        })
      }
    })
  },

  onPaste() {
    // Apply default line height to pasted content
    setTimeout(() => {
      this.options.types.forEach(type => {
        const attrs = this.editor.getAttributes(type)
        if (!attrs.lineHeight) {
          this.editor.commands.updateAttributes(type, {
            lineHeight: this.options.defaultLineHeight,
          })
        }
      })
    }, 0)
  },

  onUpdate() {
    // Ensure line height is maintained while typing
    this.options.types.forEach(type => {
      const attrs = this.editor.getAttributes(type)
      if (!attrs.lineHeight) {
        this.editor.commands.updateAttributes(type, {
          lineHeight: this.options.defaultLineHeight,
        })
      }
    })
  },
})
